import React, { useEffect, useState } from "react";
import { useTranslations } from "hooks/useTranslations";
import { useSelector } from "react-redux";
import { shoppingCartSelector } from "redux/shoppingCart";
import { contactInformationSelector } from "redux/contactInformation";
import {
  travelerInformationSelector,
  ITraveler,
} from "redux/travelerInformation";

import { selectedItinerarySelector } from "redux/selectedItinerary";
import { useLazyBookingQuery } from "redux/services/booking";
import { searchQuerySelector } from "redux/searchQuery";
import { ISearchForm, searchFormSelector } from "redux/searchForm";
import {
  additionalProductsSelector,
  IAdditionalProduct,
} from "redux/additionalProducts";

import { ResponseIconMap } from "../../ResponsePopup/ResponseIconMap";
import { useNavigate } from "react-router-dom";
import {
  IContactPerson,
  IPassengerTypeShort,
  IPaymentMethod,
  IItinerary,
  dateObjToDateStringDashedinverted,
  passengerMapShort,
  pushToDataLayer,
  useConfig,
  IconPathLoader,
  IBrandedFare,
  CtaDialog,
} from "@thekilroygroup/shared-ui";
import {
  Box,
  Button,
  DialogContent,
  SxProps,
  Dialog as MuiDialog,
} from "@mui/material";
import { format } from "date-fns";
import { getUserDevice } from "../../MetapartnerTracking/metaUtils";
import { setCookie } from "utils/cookieDisclaimer";

interface IProps {
  isValid: boolean;
  sx?: SxProps;
}

export const ConfirmBooking = (props: IProps) => {
  const { isValid, sx } = props;
  const { translations } = useTranslations();
  const shoppingCart = useSelector(shoppingCartSelector);
  const contactPerson = useSelector(contactInformationSelector);
  const travelers = useSelector(travelerInformationSelector).travelers;
  const {
    itineraries,
    includesBrandedFares,
    indexOfRequestedItinerary,
    itineraryFromSearch,
  } = useSelector(selectedItinerarySelector);
  const [trigger, result] = useLazyBookingQuery();
  const { isFetching, isError, isLoading, isUninitialized, data } = result;
  const [showResponsePopup, setShowResponsePopup] = useState(false);
  const searchQuery: ISearchForm = useSelector(searchQuerySelector);
  const additionalProducts = useSelector(additionalProductsSelector);
  const searchForm = useSelector(searchFormSelector);
  const { appConfig } = useConfig();

  const navigate = useNavigate();

  const handleBook = () => {
    trigger({
      booking: createBookingObject(),
      searchQuery: searchQuery,
      searchRequestId: searchForm.searchRequestId,
      config: appConfig,
    });
  };

  useEffect(() => {
    if (isError && !isFetching && !isLoading && !isUninitialized) {
      setShowResponsePopup(true);
    } else if (
      !isError &&
      !isFetching &&
      !isLoading &&
      !isUninitialized &&
      data
    ) {
      sessionStorage.setItem("wentToPayment", "true");
      sessionStorage.setItem("bookingId", data.id); // from booking response
      if (data && data.confirmationToken) {
        setCookie(
          "confirmationToken",
          data.confirmationToken.token,
          new Date(data.confirmationToken.expires),
        );
      }
      window.location.href = result.data.paymentLink;
    }
  }, [result]);

  const getItinerary = (baggageAllowed: boolean) => {
    return (
      itineraries.find(
        (itinerary) => itinerary.baggageAllowed === baggageAllowed,
      ) || null
    );
  };

  const trimmedPhoneNumber =
    contactPerson.phoneNumber?.national?.replace(/\D/g, "") || "";

  const createBookingObject = () => {
    const contactInformationData: IContactPerson = {
      gender: contactPerson.genderType,
      firstName: contactPerson.firstName,
      middleName: contactPerson.middleName,
      lastName: contactPerson.lastName,
      phoneCountryCode: String(contactPerson.phoneNumber?.countryCode),
      phoneNo: trimmedPhoneNumber,
      email: contactPerson.email,
    };

    const passengers: IPassenger[] = travelers.map(
      (traveler: ITraveler, passengerIndex: number) => ({
        firstName: traveler.formFields.firstName,
        middleName: traveler.formFields.middleName,
        lastName: traveler.formFields.lastName,
        dateOfBirth: format(
          new Date(traveler.formFields.dateOfBirth as string),
          "dd-MM-yyyy",
        ),
        passengerType: traveler.passengerType as IPassengerTypeShort,
        gender: traveler.formFields.genderType?.toUpperCase(),
        includeBaggage: false,
        passengerProducts: getPassengerProducts(
          passengerIndex,
          traveler.passengerType as IPassengerTypeShort,
        ),
        cardRequest: getCardRequest(traveler),
        existingCard: getExistingCard(traveler),
      }),
    );

    const booking: IBookingQuery = {
      contactPerson: contactInformationData as IContactPerson,
      passengers: passengers,
      selectedPaymentMethod:
        shoppingCart.selectedPaymentMethod as IPaymentMethod,
      bookingProducts: getBookingProducts(),
      language: translations.locale,
      device: getUserDevice(),
      ...getItineraryDetails(),
    };

    return booking;
  };

  const getItineraryDetails = () => {
    if (!includesBrandedFares) {
      return {
        itineraryNonBag: getItinerary(false),
        itineraryBag: getItinerary(true),
      };
    } else {
      return {
        brandedFareItinerary: itineraries[indexOfRequestedItinerary],
        revalidatedBrandedFare:
          itineraryFromSearch?.brandedFare as IBrandedFare,
      };
    }
  };

  const getCardRequest = (traveler: ITraveler) => {
    if (traveler.passengerType !== "STU") return null;
    const { hasStudentCard, nameOfSchool, email } = traveler.formFields;
    if (hasStudentCard === "no" && email && nameOfSchool) {
      return {
        cardType: "ISIC",
        email: email,
        placeOfStudy: nameOfSchool,
      };
    } else return null;
  };

  const getExistingCard = (traveler: ITraveler) => {
    function formatISICnumber(input: string) {
      /**
       * TODO remove this function when masking is implemented on the input field
       * @param input
       * Make sure that ISIC number is correctly formatted during to lack masking in the input field
       * @returns
       */
      const nextInput = input.replaceAll(" ", "");
      const firstChar = nextInput[0];
      const lastChar = nextInput[nextInput.length - 1];
      let number = nextInput.slice(1, -1);
      let formatted = number.replace(/(\d{3})(?=\d)/g, "$1 ");
      return `${firstChar} ${formatted} ${lastChar}`;
    }

    if (traveler.passengerType !== "STU") return null;
    const { hasStudentCard, discountCardNumber, cardExpiryDate } =
      traveler.formFields;
    if (hasStudentCard === "yes" && discountCardNumber && cardExpiryDate) {
      const date = dateObjToDateStringDashedinverted(
        new Date(cardExpiryDate),
      ) as string;

      return {
        cardType: "ISIC",
        cardNumber: formatISICnumber(discountCardNumber),
        cardExpirationDate: date as string,
      };
    } else return null;
  };

  const getBookingProducts = () => {
    let products: IProduct[] = [];
    additionalProducts.products.forEach(
      (additionalProduct: IAdditionalProduct, i: number) => {
        if (
          additionalProduct.scope === "BOOKING" &&
          additionalProduct.selectedList.includes("BOOKING")
        ) {
          products.push({
            scope: "BOOKING",
            productCode: additionalProduct.productCode,
            price: additionalProduct.bookingPrice,
          });
        }
      },
    );
    return products;
  };

  const getPassengerProducts = (
    passengerIndex: number,
    passengerTypeShort: IPassengerTypeShort,
  ) => {
    let products: IProduct[] = [];
    additionalProducts.products.forEach(
      (additionalProduct: IAdditionalProduct, i: number) => {
        if (
          additionalProduct.scope === "PASSENGER" &&
          additionalProduct.selectedList.includes(passengerIndex)
        ) {
          const passengerTypeLong = passengerMapShort[passengerTypeShort];
          const passengerProductPrice =
            additionalProduct.passengerPriceList.find(
              (item) => item.passengerGroup === passengerTypeLong,
            )?.price;

          products.push({
            scope: "PASSENGER",
            productCode: additionalProduct.productCode,
            price: passengerProductPrice as number,
          });
        }
      },
    );

    return products;
  };

  const clickNextButton = () => {
    pushToDataLayer("Step 4 - Confirm booking", "Click next step");
    handleBook();
  };

  const errorTranslations = translations.customResponseCodes["4006"];
  return (
    <Box sx={{ px: { xs: 2, md: 0 }, my: { md: 2 }, ...sx }}>
      <Button
        color="primary"
        size="xLarge"
        variant="contained"
        fullWidth={true}
        className="proceedButton"
        disabled={!isValid}
        disableElevation
        onClick={clickNextButton}
        sx={{ ml: "auto", display: "block" }}
      >
        {translations.booking.buttonText}
      </Button>
      <MuiDialog open={isFetching} maxWidth="md" fullWidth>
        <DialogContent>
          <IconPathLoader
            title={translations.booking.loaderTitle}
            text={translations.booking.loaderSubtitle}
          />
        </DialogContent>
      </MuiDialog>
      {showResponsePopup && (
        <CtaDialog
          {...errorTranslations}
          icon={
            (errorTranslations.icon &&
              ResponseIconMap[errorTranslations.icon]) ||
            undefined
          }
          onClose={() => {
            setShowResponsePopup(false);
          }}
          ctaAction={() => {
            setShowResponsePopup(false);
            navigate(`/`);
          }}
        />
      )}
    </Box>
  );
};

export interface IPassenger {
  dateOfBirth: string;
  passengerType: IPassengerTypeShort;
  gender?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  includeBaggage: boolean;
  passengerProducts: IProduct[];
  cardRequest: ICardRequest | null;
  existingCard: IExistingCard | null;
}

export interface IBookingQuery {
  contactPerson: IContactPerson;
  passengers: IPassenger[];
  selectedPaymentMethod: IPaymentMethod;
  itineraryNonBag?: IItinerary | null;
  itineraryBag?: IItinerary | null;
  brandedFareItinerary?: IItinerary;
  revalidatedBrandedFare?: IBrandedFare;
  bookingProducts: IProduct[];
  device: string;
  language: string;
}

export interface ICardRequest {
  cardType: string;
  placeOfStudy: string;
  email: string;
}
export interface IExistingCard {
  cardType: string;
  cardNumber: string;
  cardExpirationDate: string;
}

export interface IProduct {
  scope: "BOOKING" | "PASSENGER";
  productCode: String;
  price: number;
}
